/** @typedef {{ v: 1, files: string[] }} RequestPayload */

export const PAYLOAD_VERSION = 1

/**
 * Basename from a dropzone path or File.name (keeps the extension).
 * @param {{ path?: string, name?: string }} file
 * @returns {string}
 */
export function getFileBasename(file) {
  const raw = file?.path || file?.name || ''
  const segments = raw.split(/[/\\]/)
  return segments[segments.length - 1] || ''
}

/**
 * Build the stored picks payload from selected files.
 * @param {Array<{ path?: string, name?: string }>} filesList
 * @returns {RequestPayload}
 */
export function createPayloadFromFiles(filesList) {
  if (!Array.isArray(filesList)) {
    throw new TypeError('createPayloadFromFiles expects a files array')
  }

  const files = filesList
    .map(getFileBasename)
    .map((name) => name.trim())
    .filter(Boolean)

  return { v: PAYLOAD_VERSION, files }
}

/**
 * @param {unknown} payload
 * @returns {payload is RequestPayload}
 */
export function hasPayload(payload) {
  return Boolean(
    payload
    && typeof payload === 'object'
    && Array.isArray(payload.files)
    && payload.files.length > 0
  )
}

/**
 * @param {string} filename
 * @returns {string}
 */
export function stripFilenameExtension(filename) {
  return filename.replace(/\.[^.]+$/, '')
}

/**
 * Format stored picks for clipboard / external tools (e.g. Lightroom).
 * @param {unknown} payload
 * @param {object} [options]
 * @param {boolean} [options.keepExtensions=false]
 * @param {string} [options.delimiter=',']
 * @returns {string}
 */
export function formatPayload(payload, options = {}) {
  const { keepExtensions = false, delimiter = ',' } = options
  if (!hasPayload(payload)) return ''

  return payload.files
    .map((name) => (keepExtensions ? name : stripFilenameExtension(name)))
    .join(delimiter)
}
